import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getWeekStart, addDaysYMD, parseYMD, toYMD } from '../lib/utils';
import WeekJumper from './WeekJumper';

/**
 * Unified date filter for Summary/Deductions pages.
 *
 * Modes:
 *   - 'week'  : navigate week-by-week (legacy behavior)
 *   - 'range' : pick a custom start/end date with preset chips (Today, This Month, etc.)
 *
 * Props:
 *   mode, onModeChange, weekStart, onWeekStartChange, startDate, endDate, onRangeChange
 */
export default function DateRangePicker({
  mode,
  onModeChange,
  weekStart,
  onWeekStartChange,
  startDate,
  endDate,
  onRangeChange,
}) {
  const navigateWeek = (dir) => {
    onWeekStartChange(addDaysYMD(weekStart, dir * 7));
  };

  const fmt = (d) => toYMD(d);

  const applyPreset = (preset) => {
    const today = new Date();
    let s, e;
    switch (preset) {
      case 'today':
        s = e = new Date(today);
        break;
      case 'this_week': {
        const monday = getWeekStart();
        s = parseYMD(monday);
        e = parseYMD(addDaysYMD(monday, 6));
        break;
      }
      case 'last_week': {
        const monday = addDaysYMD(getWeekStart(), -7);
        s = parseYMD(monday);
        e = parseYMD(addDaysYMD(monday, 6));
        break;
      }
      case 'this_month':
        s = new Date(today.getFullYear(), today.getMonth(), 1);
        e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_month':
        s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        e = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last_30':
        s = new Date(today);
        s.setDate(s.getDate() - 29);
        e = new Date(today);
        break;
      case 'ytd':
        s = new Date(today.getFullYear(), 0, 1);
        e = new Date(today);
        break;
      default:
        return;
    }
    onRangeChange(fmt(s), fmt(e));
  };

  return (
    <div className="space-y-2" data-testid="date-range-picker">
      {/* Mode toggle */}
      <div className="inline-flex rounded-lg border bg-muted/30 p-0.5">
        <button
          type="button"
          onClick={() => onModeChange('week')}
          data-testid="mode-week-btn"
          className={`h-7 px-3 text-xs font-medium rounded-md transition-colors ${
            mode === 'week'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-foreground hover:bg-muted'
          }`}
        >
          Week
        </button>
        <button
          type="button"
          onClick={() => {
            onModeChange('range');
            // Default to "this month" if no range yet
            if (!startDate || !endDate) applyPreset('this_month');
          }}
          data-testid="mode-range-btn"
          className={`h-7 px-3 text-xs font-medium rounded-md transition-colors ${
            mode === 'range'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-foreground hover:bg-muted'
          }`}
        >
          Custom Range
        </button>
      </div>

      {/* Week mode: original navigator */}
      {mode === 'week' ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <WeekJumper weekStart={weekStart} onChange={onWeekStartChange} />
          <Button variant="outline" size="icon" onClick={() => navigateWeek(1)} data-testid="next-week-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => onWeekStartChange(getWeekStart())} data-testid="today-btn">
            Today
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Preset chips */}
          <div className="flex flex-wrap gap-1.5" data-testid="range-presets">
            {[
              { k: 'today', l: 'Today' },
              { k: 'this_week', l: 'This Week' },
              { k: 'last_week', l: 'Last Week' },
              { k: 'this_month', l: 'This Month' },
              { k: 'last_month', l: 'Last Month' },
              { k: 'last_30', l: 'Last 30 Days' },
              { k: 'ytd', l: 'Year to Date' },
            ].map((p) => (
              <Button
                key={p.k}
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => applyPreset(p.k)}
                data-testid={`preset-${p.k}`}
              >
                {p.l}
              </Button>
            ))}
          </div>
          {/* Range inputs */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">From</label>
              <Input
                type="date"
                value={startDate || ''}
                onChange={(e) => onRangeChange(e.target.value, endDate)}
                className="h-9 w-[160px]"
                data-testid="range-start-input"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">To</label>
              <Input
                type="date"
                value={endDate || ''}
                onChange={(e) => onRangeChange(startDate, e.target.value)}
                className="h-9 w-[160px]"
                data-testid="range-end-input"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
